import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, GitBranch, LoaderCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';


const requestJson = async (path, { method = 'GET', body } = {}) => {
  const response = await apiFetch(path, {
    method,
    headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    throw new Error(
      (detail && typeof detail === 'object' ? detail.message : detail)
      || payload?.error
      || `Request failed (${response.status})`
    );
  }
  return payload;
};

export const recordedFlowsAvailableForComposition = (sources = []) => (
  (Array.isArray(sources) ? sources : []).filter((source) => (
    String(source?.status || '').toLowerCase() !== 'archived'
    && Number(source?.counts?.steps || 0) > 0
  ))
);

export const moveRecordedFlow = (ids, index, offset) => {
  const destination = index + offset;
  if (destination < 0 || destination >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
};

export default function RecordedFlowBuilder({ application, sources, onClose, onCreated }) {
  const available = useMemo(() => recordedFlowsAvailableForComposition(sources), [sources]);
  const byId = useMemo(() => new Map(
    available.map((source) => [String(source.id), source])
  ), [available]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [flowName, setFlowName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = (sourceId) => {
    setSelectedIds((current) => (
      current.includes(sourceId)
        ? current.filter((id) => id !== sourceId)
        : [...current, sourceId]
    ));
  };

  const createFlow = async () => {
    if (!selectedIds.length || !flowName.trim() || !application?.id) return;
    setBusy(true);
    setError('');
    try {
      const imported = await requestJson(API_ENDPOINTS.FLOW_IMPORT_CAPTURES, {
        method: 'POST',
        body: { application_id: application.id, capture_ids: selectedIds },
      });
      const items = Array.isArray(imported?.items) ? imported.items : [];
      if (items.length !== selectedIds.length) {
        throw new Error('Some recorded flows could not be prepared as reusable components.');
      }
      const payload = await requestJson(API_ENDPOINTS.FLOW_COMPOSE, {
        method: 'POST',
        body: {
          feature_name: flowName.trim(),
          application_id: application.id,
          nodes: items.map((item, index) => ({
            id: `node-${index + 1}`,
            module_version_id: item.module_version_id,
            alias: item.capture_name || '',
            input_bindings: {},
          })),
        },
      });
      onCreated?.(payload);
    } catch (buildError) {
      setError(buildError?.message || 'Could not create the modular flow.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="recorded-flow-builder-title">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-indigo-600" />
              <h2 id="recorded-flow-builder-title" className="text-lg font-bold text-slate-900">Create flow from recordings</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">Put a shared recording, such as login and navigation, first. Add a continuation recording after it. Reuse the same shared recording in as many flows as you need.</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} disabled={busy} aria-label="Close flow builder"><X className="h-4 w-4" /></button>
        </header>

        <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-5 lg:grid-cols-2">
          <section>
            <h3 className="text-sm font-bold text-slate-900">Recorded flows</h3>
            <div className="mt-3 space-y-2">
              {available.map((source) => {
                const sourceId = String(source.id);
                const checked = selectedIds.includes(sourceId);
                return (
                  <label key={sourceId} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${checked ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(sourceId)} disabled={busy} />
                    <span className="min-w-0">
                      <strong className="block truncate text-sm text-slate-900">{source.title}</strong>
                      <small className="text-xs text-slate-500">{Number(source?.counts?.steps || 0)} recorded steps</small>
                    </span>
                  </label>
                );
              })}
              {!available.length && <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No completed recordings with steps are available.</p>}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-bold text-slate-900">Execution order</h3>
            <div className="mt-3 space-y-2">
              {selectedIds.map((sourceId, index) => (
                <div key={sourceId} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">{index + 1}</span>
                  <strong className="min-w-0 flex-1 truncate text-sm text-slate-900">{byId.get(sourceId)?.title || sourceId}</strong>
                  <button type="button" onClick={() => setSelectedIds((ids) => moveRecordedFlow(ids, index, -1))} disabled={busy || index === 0} aria-label={`Move ${byId.get(sourceId)?.title || sourceId} up`}><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setSelectedIds((ids) => moveRecordedFlow(ids, index, 1))} disabled={busy || index === selectedIds.length - 1} aria-label={`Move ${byId.get(sourceId)?.title || sourceId} down`}><ArrowDown className="h-4 w-4" /></button>
                </div>
              ))}
              {!selectedIds.length && <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">Select one or more recordings to begin.</p>}
            </div>
            <label className="mt-4 block">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-600">New flow name</span>
              <input className="text-input mt-1" value={flowName} onChange={(event) => setFlowName(event.target.value)} maxLength={160} placeholder="Example: Complete order journey" disabled={busy} />
            </label>
            {error && <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">{error}</p>}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" className="secondary-btn px-4 py-2 text-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary-btn px-4 py-2 text-sm" onClick={createFlow} disabled={busy || !selectedIds.length || !flowName.trim()}>
            {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />}
            {busy ? 'Creating flow…' : 'Create flow'}
          </button>
        </footer>
      </div>
    </div>
  );
}
